<script setup lang="ts">
import { computed, ref } from 'vue';
import { Address } from '@/application/shared/domain/Address';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { Field } from 'vee-validate';
import { LocationSelector } from '@hfc/ui-core';
import { useCheckCoverage } from '@/quote/infrastructure/composition/useCheckCoverage';

const { checkCoverage } = useCheckCoverage();

const props = defineProps<{
  value?: Partial<Address>;
  zipReadOnly?: boolean,
  id: string;
  applicationType: APPLICATION_TYPES;
  validateCoverage?: boolean;
  disablePlaceholder?: boolean;
}>();

const city = ref(props.value?.city);
const state = ref(props.value?.state || 'CA');
const county = ref(props.value?.county);
const zip = ref(props.value?.zip_code);
const countyVisible = ref(false);

const customZipClass = computed(() => `h-14 ${countyVisible.value ? 'middle' : 'right'}`);
const customLocationClass = computed(() => countyVisible.value ? 'w-80' : 'w-36');

interface EmitInterface {
  (event: 'update:value', value: Partial<Address>): void
  (event: 'setLoading', value: boolean): void;
}

const emits = defineEmits<EmitInterface>();

const cityPlaceholder = props.disablePlaceholder ? '' : 'City';
const zipcodePlaceholder = props.disablePlaceholder ? '' : 'Zipcode';

const handleZipcodeChange = (value: number): void => {
  zip.value = value;
  emits('update:value', { zip_code: Number(zip.value) });
};

const handleCountyChange = (value: string): void => {
  county.value = value;
  emits('update:value', { county: county.value });
};

const emitChange = (): void => {
  emits('update:value', {
    county: county.value,
    city: city.value,
    zip_code: Number(zip.value),
    state: state.value
  });
};

</script>
<template>
  <div class="form-inline">
    <div class="flex flex-col md:flex-1">
      <Field type="text" :name="`${id}-city`" v-model="city" @change="emitChange" v-slot="{ field, meta, errorMessage }" rules="required|alpha_spaces">
        <input v-bind="field" v-capitalized="field" class="left" :class="{ ...meta }" :placeholder="cityPlaceholder" />
        <span class="error">{{ errorMessage }}</span>
      </Field>
    </div>
    <div class="flex flex-col md:w-32">
      <Field :name="`${id}-state`" v-model="state" @change="emitChange" class="middle w-auto" v-slot="{ field, meta, errorMessage }" rules="required">
        <select class="middle w-auto" v-bind="field" :class="{ ...meta }">
          <option value="CA">CA</option>
        </select>
        <span class="error">{{ errorMessage }}</span>
      </Field>
    </div>
    <div class="flex flex-col" :class="customLocationClass">
      <LocationSelector
        ref="locationSelectorRef"
        :id="id"
        @countyVisible="countyVisible = $event"
        :custom-zip-class="customZipClass"
        :zip-read-only="zipReadOnly"
        custom-county-class="h-14 right"
        :placeholder="zipcodePlaceholder"
        :hide-labels="true"
        :zipcode="zip"
        :county="county"
        :application-type="applicationType"
        :validate-coverage="validateCoverage"
        :check-coverage="checkCoverage"
        @set-loading="emits('setLoading', $event)"
        @update:zip-code="handleZipcodeChange"
        @update:county="handleCountyChange" />
    </div>
  </div>
</template>
